"use client"
import CloseIcon from "@mui/icons-material/Close"

import {
  Divider,
  Drawer,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material"
import { useUserInfo, useViewAllRequest } from "@tc/ui-dfe/hooks"
import {
  AddIcon,
  CloseIcon as DrawerCloseIcon,
  FilterIcon,
  ArrowDownIcon,
  ArrowUpIcon,
} from "@tc/ui-dfe/icons"

import {
  Button,
  Chip,
  ConfigTypesenseRefineSelect as TypesenseRefineSelect,
} from "@tc/ui-dfe/components"
import {
  TypesenseRefineCheckbox,
  TypesenseRefineDate,
} from "@tc/ui-dfe/components/TypesenseRefinements"
import { usePermission } from "@tc/ui-permission/hooks"
import { FEATURE_FLAGS, ROLE } from "@tc/ui-permission/utils"
import dayjs from "@tc/ui-dfe/utils/libs/dayjs"

import { useEffect, useRef, useState } from "react"
import { useRange, useRefinementList } from "react-instantsearch"
import { FilterChipLabels, NONCONSIGNMENT_CERTIFICATE_TYPE } from "./Constants"

const titleSx = { fontSize: 18, lineHeight: 1.55, mb: 0.5 }
const buttonSx = { fontSize: 16, lineHeight: 1.5, px: 2.5 }

export const Filters = () => {
  const { clearFiltersToggle, requestsCount, clearRefinements } =
    useViewAllRequest()
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"))

  const { hasPermission, hasAccess } = usePermission()
  const isAppHytoEnabled = hasPermission([FEATURE_FLAGS.FEATURE_FLAG_APPHYTO])
  const isOCAUser = hasAccess([ROLE.OCABorder])
  const { registrationArray } = useUserInfo()

  const theme = useTheme()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const onCloseDrawer = () => {
    setIsDrawerOpen(false)
  }
  const onOpenDrawer = () => {
    setIsDrawerOpen(true)
  }

  const clearFiltersHandler = () => {
    sessionStorage.setItem("lastRequestsURL", "")
    // Note: sessionStorage filter state will be cleared automatically by onStateChange
    // when clearRefinements() triggers a UI state update with no filters
    clearRefinements()
  }

  const drawerClearFiltersHandler = () => {
    sessionStorage.setItem("lastRequestsURL", "")
    // Note: sessionStorage filter state will be cleared automatically by onStateChange
    // when clearRefinements() triggers a UI state update with no filters
    clearRefinements()
    onCloseDrawer()
  }

  // please keep these hooks, as they keep the filter status after components unmounted
  useRefinementList({ attribute: "status_label" })
  useRefinementList({ attribute: "country_name" })
  useRefinementList({ attribute: "organisation" })
  useRefinementList({ attribute: "transport_mode" })
  useRange({ attribute: "departure_ts" })
  useRange({ attribute: "approved_at" })
  // Always initialize third party refinement hook to maintain state consistency
  useRefinementList({ attribute: "third_party_consignor" })
  // OCA-specific filter hooks
  useRefinementList({ attribute: "inspection_port_filter" })
  useRefinementList({ attribute: "discharge_port_filter" })
  // Non-consignment filter hook
  useRefinementList({ attribute: "certificate_type" })

  const addFilterButton =
    (isAppHytoEnabled || isOCAUser) && mdUp ? (
      <Button
        ariaLabel="filter"
        variant="outlined"
        color="secondary"
        data-testid="filter-button-testid"
        startIcon={<AddIcon />}
        sx={{ height: "32px", px: 1.5 }}
        onClick={onOpenDrawer}
      >
        Add Filter
      </Button>
    ) : null

  return (
    <>
      <Stack gap={1.5} flexDirection="row" flexWrap="wrap" alignItems="center">
        {(isAppHytoEnabled || isOCAUser) && mdUp ? (
          <AppliedFilters
            addFilterButton={addFilterButton}
            clearFilters={clearFiltersHandler}
          />
        ) : (
          <Button
            ariaLabel="filter"
            variant="outlined"
            color="secondary"
            data-testid="filter-button-testid"
            startIcon={<FilterIcon />}
            endIcon={
              <Chip
                sx={{
                  verticalAlign: "baseline",
                  px: 0.5,
                  py: { xs: 0, md: 1 },
                  height: { xs: 16, md: 24 },
                  "& .MuiChip-label": {
                    p: 0,
                    fontSize: { xs: 14, md: 16 },
                    lineHeight: { xs: 1.15, md: 1.5 },
                  },
                }}
                label={requestsCount}
                color="success"
              />
            }
            sx={{
              "& .MuiButton-endIcon": {
                width: "auto",
                lineHeight: { xs: 2, md: 3 },
              },
            }}
            onClick={onOpenDrawer}
          >
            Filters
          </Button>
        )}
      </Stack>
      <Drawer
        PaperProps={{
          sx: {
            width: {
              xs: "100%",
              sm: 375,
            },
            gap: 2,
          },
        }}
        anchor="right"
        component="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault()
          onCloseDrawer()
        }}
        open={isDrawerOpen}
        onClose={onCloseDrawer}
      >
        <Stack
          direction="row"
          gap={1}
          alignItems="start"
          sx={{ p: 3.5, pb: 0 }}
        >
          <FilterIcon />
          <Typography
            variant="h2"
            sx={{ fontSize: { xs: 24 }, lineHeight: 1.33 }}
          >
            Filter
          </Typography>
          <Button
            ariaLabel="close-button"
            sx={{ ml: "auto", p: 0, minWidth: 24, width: 24, height: 24 }}
            disableRipple
            disableFocusRipple
            onClick={onCloseDrawer}
          >
            <DrawerCloseIcon
              sx={{ "&.MuiSvgIcon-root": { width: 24, height: 24 } }}
            />
          </Button>
        </Stack>

        <Divider />

        <Stack gap={1} sx={{ overflowY: "auto", p: 3.5, pt: 0 }}>
          {isOCAUser ? (
            <>
              <Stack>
                <Typography variant="h4" sx={titleSx}>
                  Inspection port
                </Typography>
                <TypesenseRefineSelect
                  multiple
                  clearFiltersToggle={clearFiltersToggle}
                  attribute={"inspection_port_filter"}
                  limit={20}
                  showMore={true}
                  showMoreLimit={300}
                />
              </Stack>

              <Stack>
                <Typography variant="h4" sx={titleSx}>
                  Port of Discharge
                </Typography>
                <TypesenseRefineSelect
                  multiple
                  clearFiltersToggle={clearFiltersToggle}
                  attribute={"discharge_port_filter"}
                  limit={20}
                  showMore={true}
                  showMoreLimit={300}
                />
              </Stack>

              <Stack>
                <Typography variant="h4" sx={titleSx}>
                  Status
                </Typography>
                <TypesenseRefineSelect
                  multiple
                  clearFiltersToggle={clearFiltersToggle}
                  attribute={"status_label"}
                  limit={20}
                  showMore={true}
                  showMoreLimit={50}
                  showCancelledStatus={false}
                />
              </Stack>

              <Stack>
                <Typography variant="h4" sx={titleSx}>
                  Departure date
                </Typography>
                <TypesenseRefineDate attribute={"departure_ts"} />
              </Stack>

              <Stack>
                <Typography variant="h4" sx={titleSx}>
                  Approval date
                </Typography>
                <TypesenseRefineDate attribute={"approved_at"} />
              </Stack>
            </>
          ) : (
            <>
              <Stack>
                <Typography variant="h4" sx={titleSx}>
                  Request status
                </Typography>
                <TypesenseRefineSelect
                  multiple
                  clearFiltersToggle={clearFiltersToggle}
                  attribute={"status_label"}
                  limit={20}
                  showMore={true}
                  showMoreLimit={50}
                  showCancelledStatus={true}
                />
              </Stack>

              {isAppHytoEnabled && (
                <Stack>
                  <Typography variant="h4" sx={titleSx}>
                    Consignor
                  </Typography>
                  <TypesenseRefineSelect
                    multiple
                    clearFiltersToggle={clearFiltersToggle}
                    attribute={"organisation"}
                    limit={20}
                    showMore={true}
                    showMoreLimit={300}
                  />
                </Stack>
              )}

              <Stack>
                <Typography variant="h4" sx={titleSx}>
                  Destination
                </Typography>
                <TypesenseRefineSelect
                  multiple
                  showMore
                  showMoreLimit={300}
                  clearFiltersToggle={clearFiltersToggle}
                  attribute={"country_name"}
                />
              </Stack>

              {isAppHytoEnabled && (
                <>
                  <Stack>
                    <Typography variant="h4" sx={titleSx}>
                      Transport mode
                    </Typography>
                    <TypesenseRefineSelect
                      multiple
                      clearFiltersToggle={clearFiltersToggle}
                      attribute={"transport_mode"}
                    />
                  </Stack>

                  <Stack>
                    <Typography variant="h4" sx={titleSx}>
                      Departure date
                    </Typography>
                    <TypesenseRefineDate attribute={"departure_ts"} />
                  </Stack>
                  <Stack>
                    <Typography variant="h4" sx={titleSx}>
                      Approval date
                    </Typography>
                    <TypesenseRefineDate attribute={"approved_at"} />
                  </Stack>

                  <Stack>
                    <TypesenseRefineCheckbox
                      attribute="third_party_consignor"
                      values={registrationArray ?? []}
                      clearFiltersToggle={clearFiltersToggle}
                      label="Only show 3rd party requests"
                      subtitle="Only applies to animal product requests"
                    />
                  </Stack>

                  <Stack>
                    <TypesenseRefineCheckbox
                      attribute="certificate_type"
                      values={[NONCONSIGNMENT_CERTIFICATE_TYPE]}
                      clearFiltersToggle={clearFiltersToggle}
                      label="Show non-consignment requests only"
                    />
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          mt="auto"
          sx={{ p: 3.5, pt: 0 }}
        >
          <Button
            sx={{
              ...buttonSx,
              fontWeight: 600,
              color: theme.palette.error.main,
            }}
            ariaLabel="clear-all"
            variant="text"
            color="warning"
            type="button"
            onClick={drawerClearFiltersHandler}
          >
            Clear all
          </Button>
          <Button
            sx={buttonSx}
            ariaLabel="apply-filters"
            variant="contained"
            onClick={() => onCloseDrawer()}
          >
            Apply filters
          </Button>
        </Stack>
      </Drawer>
    </>
  )
}

interface AppliedFiltersProps {
  clearFilters: () => void
  addFilterButton: React.ReactElement | null
}

const AppliedFilters = ({
  clearFilters,
  addFilterButton,
}: AppliedFiltersProps) => {
  const theme = useTheme()

  const chipRefs = useRef<(HTMLDivElement | null)[]>([])
  const [expanded, setExpanded] = useState(false)
  const { registrationArray } = useUserInfo()

  const statusRefinement = useRefinementList({ attribute: "status_label" })
  const organisationRefinement = useRefinementList({
    attribute: "organisation",
  })
  const countryRefinement = useRefinementList({
    attribute: "country_name",
  })
  const transportModeRefinement = useRefinementList({
    attribute: "transport_mode",
  })
  const departureDateRefinement = useRange({
    attribute: "departure_ts",
  })
  const approvalDateRefinement = useRange({ attribute: "approved_at" })
  const thirdPartyRefinement = useRefinementList({
    attribute: "third_party_consignor",
  })
  const inspectionPortRefinement = useRefinementList({
    attribute: "inspection_port_filter",
  })
  const dischargePortRefinement = useRefinementList({
    attribute: "discharge_port_filter",
  })
  const certificateTypeRefinement = useRefinementList({
    attribute: "certificate_type",
  })

  const dateFormat = "DD/MM/YYYY"

  const getFormattedValueForDate = (isDeparture: boolean) => {
    const refinement = isDeparture
      ? departureDateRefinement
      : approvalDateRefinement
    const [startRange, endRange] = refinement.start
    const { min, max } = refinement.range
    if (startRange === -Infinity && endRange === Infinity) {
      return ""
    }
    return `${dayjs(startRange === -Infinity ? min : startRange)
      .tz("Pacific/Auckland")
      .format(dateFormat)}-${dayjs(endRange === Infinity ? max : endRange)
      .tz("Pacific/Auckland")
      .format(dateFormat)}`
  }

  const selectedFilters = [
    ...statusRefinement.items
      .filter((item) => item.isRefined)
      .map((item) => ({
        attribute: "status_label",
        value: item.label,
        refinementValue: item.value,
      })),
    ...organisationRefinement.items
      .filter((item) => item.isRefined)
      .map((item) => ({
        attribute: "organisation",
        value: item.label,
        refinementValue: item.value,
      })),
    ...countryRefinement.items
      .filter((item) => item.isRefined)
      .map((item) => ({
        attribute: "country_name",
        value: item.label,
        refinementValue: item.value,
      })),
    ...transportModeRefinement.items
      .filter((item) => item.isRefined)
      .map((item) => ({
        attribute: "transport_mode",
        value: item.label,
        refinementValue: item.value,
      })),
    {
      attribute: "departure_ts",
      value: getFormattedValueForDate(true),
      refinementValue: "",
    },
    {
      attribute: "approved_at",
      value: getFormattedValueForDate(false),
      refinementValue: "",
    },
    ...(thirdPartyRefinement.items.some((item) => item.isRefined)
      ? [
          {
            attribute: "third_party_consignor",
            value: "3rd Party Consigner",
            refinementValue: "all",
          },
        ]
      : []),
    ...inspectionPortRefinement.items
      .filter((item) => item.isRefined)
      .map((item) => ({
        attribute: "inspection_port_filter",
        value: item.label,
        refinementValue: item.value,
      })),
    ...dischargePortRefinement.items
      .filter((item) => item.isRefined)
      .map((item) => ({
        attribute: "discharge_port_filter",
        value: item.label,
        refinementValue: item.value,
      })),
    ...(certificateTypeRefinement.items.some(
      (item) =>
        item.value === NONCONSIGNMENT_CERTIFICATE_TYPE && item.isRefined,
    )
      ? [
          {
            attribute: "certificate_type",
            value: "Non consignment requests",
            refinementValue: NONCONSIGNMENT_CERTIFICATE_TYPE,
          },
        ]
      : []),
  ].filter((selectedFilter) => selectedFilter.value)

  const handleChipDelete = (attribute: string, value: string) => {
    const { min, max } = departureDateRefinement.range
    const { min: approvalMin, max: approvalMax } = approvalDateRefinement.range

    switch (attribute) {
      case "status_label":
        statusRefinement.refine(value)
        break
      case "organisation":
        organisationRefinement.refine(value)
        break
      case "country_name":
        countryRefinement.refine(value)
        break
      case "transport_mode":
        transportModeRefinement.refine(value)
        break
      case "departure_ts":
        departureDateRefinement.refine([min, max])
        break
      case "approved_at":
        approvalDateRefinement.refine([approvalMin, approvalMax])
        break
      case "third_party_consignor": {
        registrationArray?.forEach((value) =>
          thirdPartyRefinement.refine(value),
        )
        break
      }
      case "inspection_port_filter":
        inspectionPortRefinement.refine(value)
        break
      case "discharge_port_filter":
        dischargePortRefinement.refine(value)
        break
      case "certificate_type":
        certificateTypeRefinement.refine(value)
        break
    }
  }
  const [visibleCount, setVisibleCount] = useState(0)

  useEffect(() => {
    const calculateVisibleItems = () => {
      if (selectedFilters.length <= visibleCount && expanded) {
        expanded && setExpanded(false)
        return
      }
      const items = chipRefs.current
      if (items.length < 1) return

      const firstItem = items[0] as HTMLDivElement
      if (!firstItem) return
      const firstTop = firstItem.offsetTop
      let count = 0

      items.forEach((item_temp, index) => {
        const item = item_temp as HTMLDivElement
        if (!item) return
        if (expanded) {
          item.style.display = "inline-flex"
        } else if (item.offsetTop !== firstTop) {
          item.style.display = "none"
        } else {
          item.style.display = "inline-flex"
          count += 1
        }

        !expanded && setVisibleCount(count)
      })
    }
    calculateVisibleItems()
    window.addEventListener("resize", calculateVisibleItems)

    return () => window.removeEventListener("resize", calculateVisibleItems)
  }, [selectedFilters, expanded])

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          maxWidth: expanded ? "100%" : "70%",
          width: "auto",
        }}
      >
        {selectedFilters.map((filter, index) => (
          <Chip
            ref={(el) => {
              chipRefs.current[index] = el as HTMLDivElement
            }}
            variant="outlined"
            key={`${filter.attribute}-${filter.value}-${index}`}
            label={
              filter.attribute === "third_party_consignor" ||
              filter.attribute === "certificate_type"
                ? FilterChipLabels[filter.attribute]
                : `${FilterChipLabels[filter.attribute]}: ${filter.value}`
            }
            onDelete={() =>
              handleChipDelete(filter.attribute, filter.refinementValue)
            }
            deleteIcon={
              <Stack
                sx={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon
                  sx={{
                    width: "16px",
                    color: (theme) => theme.palette.text.primary,
                  }}
                />
              </Stack>
            }
            sx={{
              width: "max-content",
              background: (theme) => theme.palette.footer.main,
            }}
          />
        ))}
      </Box>
      <Box>
        {!expanded && selectedFilters.length > visibleCount && (
          <Button
            sx={{
              color: theme.palette.text.primary,
            }}
            endIcon={<ArrowDownIcon />}
            ariaLabel="Show more"
            onClick={() => setExpanded(true)}
          >
            Show more
          </Button>
        )}

        {expanded && selectedFilters.length > 0 && (
          <Button
            sx={{
              color: theme.palette.text.primary,
            }}
            endIcon={<ArrowUpIcon />}
            ariaLabel="Show less"
            onClick={() => setExpanded(false)}
          >
            Show less
          </Button>
        )}
        {addFilterButton}
        {selectedFilters?.length > 0 && (
          <Button
            sx={{
              ...buttonSx,
              fontWeight: 600,
              color: theme.palette.error.main,
              order: 99,
            }}
            ariaLabel="clear-all"
            variant="text"
            color="warning"
            type="button"
            onClick={clearFilters}
          >
            Clear
          </Button>
        )}
      </Box>
    </>
  )
}
